import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { DismissFilled } from '@fluentui/react-icons';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Typography } from '@mui/material';
import { SystemForm } from '@ui/components/Forms';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { createSystem, resetSystemEditMode, setIsSystemUpdated, updateSystemDetail, fetchSystems } from '@application/redux/SystemDetail/system-detail-slice';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import useAsyncEffect from 'use-async-effect';
import { systemApi } from '@application/api/endpoints/system-api';
const SystemDialog = ({ isOpen, onClose, variant = 'add', onSystemUpdated }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const pathname = window.location.pathname;
    const segments = pathname.split('/');
    const id = segments[segments.length - 1];
    const [formData, setFormData] = useState([]);
    const [selectedSystem, setSelectedSystem] = useState();
    const methods = useForm({
        defaultValues: {
            systemName: '',
            systemType: '',
            description: '',
            assetDetailId: id,
            cameraUrl: '',
            jetsonId: '',
            id: ''
        },
        mode: 'onBlur'
    });
    const { reset, setValue } = methods;
    const { systemEditMode } = useSelector((state) => state.systemDetail);
    const handleClose = () => {
        onClose();
        methods.reset();
    };
    useEffect(() => {
        if (variant === 'edit' && systemEditMode) {
            // Reset form with all the systemEditMode data
            const formData = {
                id: systemEditMode.id,
                systemName: systemEditMode.systemName,
                systemType: systemEditMode.systemType,
                description: systemEditMode.description,
                cameraUrl: systemEditMode.cameraUrl || systemEditMode.liveStreamUrl || '',
                jetsonId: systemEditMode.jetsonId || '',
                assetDetailId: systemEditMode.assetDetailId,
                systemId: systemEditMode.systemId,
                isActive: systemEditMode.isActive,
                status: systemEditMode.status,
                isDeleted: systemEditMode.isDeleted,
                asset: systemEditMode.asset,
                lastLocation: systemEditMode.lastLocation,
                lastInfoReceived: systemEditMode.lastInfoReceived,
                cameraStatus: systemEditMode.cameraStatus
            };
            reset(formData);
            setSelectedSystem(systemEditMode);
        }
    }, [variant, systemEditMode, reset]);
    // Additional useEffect to handle dialog opening
    useEffect(() => {
        if (isOpen && variant === 'edit' && systemEditMode) {
            // Delay to ensure form is ready
            setTimeout(() => {
                const formData = {
                    id: systemEditMode.id,
                    systemName: systemEditMode.systemName,
                    systemType: systemEditMode.systemType,
                    description: systemEditMode.description,
                    cameraUrl: systemEditMode.cameraUrl || systemEditMode.liveStreamUrl || '',
                    jetsonId: systemEditMode.jetsonId || '',
                    assetDetailId: systemEditMode.assetDetailId,
                    systemId: systemEditMode.systemId,
                    isActive: systemEditMode.isActive,
                    status: systemEditMode.status,
                    isDeleted: systemEditMode.isDeleted,
                    asset: systemEditMode.asset,
                    lastLocation: systemEditMode.lastLocation,
                    lastInfoReceived: systemEditMode.lastInfoReceived,
                    cameraStatus: systemEditMode.cameraStatus
                };
                reset(formData);
                setValue('cameraUrl', systemEditMode.cameraUrl || systemEditMode.liveStreamUrl || '');
                setValue('jetsonId', systemEditMode.jetsonId || '');
                setValue('systemType', systemEditMode.systemType || '');
                setValue('systemName', systemEditMode.systemName || '');
                setValue('description', systemEditMode.description || '');
            }, 100);
        }
    }, [isOpen, variant, systemEditMode, reset, setValue]);
    const handleApiResponse = (response, successMessage) => {
        if (response.meta.requestStatus === 'fulfilled') {
            const { isSuccess, message } = response.payload;
            if (isSuccess) {
                toast.success(message || successMessage);
                handleClose();
            }
            else {
                toast.error(message);
                handleClose();
            }
        }
        else {
            toast.error(response.payload);
            handleClose();
        }
    };
    const handleEditSystem = async (data) => {
        // Convert SystemDetailsDto to updateSystemDetailCommand
        const updateCommand = {
            id: data.id,
            systemName: data.systemName,
            systemType: data.systemType,
            description: data.description,
            cameraUrl: data.cameraUrl,
            jetsonId: data.jetsonId
        };
        const response = await dispatch(updateSystemDetail(updateCommand));
        handleApiResponse(response, 'System updated successfully!');
        if (response.meta.requestStatus == 'fulfilled') {
            dispatch(setIsSystemUpdated());
            dispatch(resetSystemEditMode());
            // Call the callback to update the grid temporarily
            if (onSystemUpdated) {
                onSystemUpdated(updateCommand);
            }
            // Refresh the systems list
            dispatch(fetchSystems(id));
        }
    };
    const handleCreateSystem = async (data) => {
        const response = await dispatch(createSystem(data));
        handleApiResponse(response, 'System created successfully!');
        // Refresh the systems list after successful creation
        if (response.meta.requestStatus === 'fulfilled' && response.payload?.isSuccess) {
            dispatch(fetchSystems(id));
        }
    };
    useAsyncEffect(async () => {
        try {
            const response = await systemApi.getSystems();
            if (response.isSuccess && response?.data) {
                setFormData(response?.data);
            }
        }
        catch (error) { }
    }, []);
    const onSubmit = async (data) => {
        try {
            let payload = { ...data };
            payload.assetDetailId = id;
            if (variant === 'edit' && systemEditMode) {
                payload.id = systemEditMode.id;
                payload.systemId = systemEditMode.systemId;
                payload.isActive = systemEditMode.isActive;
                payload.status = systemEditMode.status;
                payload.isDeleted = systemEditMode.isDeleted;
                payload.asset = systemEditMode.asset;
                payload.lastLocation = systemEditMode.lastLocation;
                payload.lastInfoReceived = systemEditMode.lastInfoReceived;
                payload.cameraStatus = systemEditMode.cameraStatus;
                payload.editMode = true;
                await handleEditSystem(payload);
            }
            else {
                if (selectedSystem) {
                    payload.id = selectedSystem.id;
                    payload.systemId = selectedSystem.systemId;
                    payload.isActive = selectedSystem.isActive;
                    payload.status = selectedSystem.status;
                    payload.isDeleted = selectedSystem.isDeleted;
                    payload.asset = selectedSystem.asset;
                    payload.lastLocation = selectedSystem.lastLocation;
                    payload.lastInfoReceived = selectedSystem.lastInfoReceived;
                    payload.cameraStatus = selectedSystem.cameraStatus;
                }
                await handleCreateSystem(payload);
            }
        }
        catch (error) { }
    };
    return (_jsxs(Dialog, { open: isOpen, onClose: handleClose, maxWidth: 'sm', PaperProps: {
            sx: {
                width: '564px'
            }
        }, fullWidth: true, "aria-labelledby": 'AssetDialog', onKeyDown: (e) => variant !== 'edit' && e.key === 'Enter' && methods.handleSubmit(onSubmit)(), children: [_jsxs(DialogTitle, { id: 'add-vehicle-dialog', sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }, children: [_jsxs(Typography, { variant: 'h6', color: 'text.primary', children: [variant === 'add' ? 'Add' : 'Edit', " System"] }), _jsx(IconButton, { onClick: handleClose, children: _jsx(DismissFilled, { fontSize: 18 }) })] }), _jsx(Divider, {}), _jsx(DialogContent, { children: _jsx(Box, { className: 'form-wrapper', sx: { mt: 1 }, children: _jsx(FormProvider, { ...methods, children: _jsx(SystemForm, { formData: formData, selectedSystem: selectedSystem, setSelectedSystem: setSelectedSystem, disabled: variant === 'edit' ? true : false }) }) }) }), _jsx(Divider, {}), _jsxs(DialogActions, { sx: { p: 3 }, children: [_jsx(Button, { onClick: methods.handleSubmit(onSubmit), variant: 'contained', color: 'inherit', disabled: variant === 'edit', children: t('Confirm') }), _jsx(Button, { onClick: handleClose, color: 'inherit', variant: 'outlined', children: t('Cancel') })] })] }));
};
export default SystemDialog;
